import { useEffect, useMemo, useRef, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/NewButton';
import Loading from '@/components/shared/Loading';
import { useDockets } from '@/hooks/useDockets';
import { apiErrorMessage } from '@/lib/api-client';
import { cn } from '@/lib/utils';
import { caseStatusTone, formatDocketDate } from './format';
import type { DocketCandidate } from './schema';
import { StatusPill } from './StatusPill';

interface DocketBrowserProps {
  matterGuid: string;
  onLink: (candidate: DocketCandidate) => void | Promise<void>;
  /** Source keys (`provider:sourceDocketId`) of already-linked dockets to hide. */
  excludeKeys?: string[];
  /** Hide the internal heading/description (e.g. when rendered inside a dialog
   * that already provides its own title). */
  hideHeader?: boolean;
}

const SEARCH_DEBOUNCE_MS = 800;

const candidateKey = (candidate: DocketCandidate): string =>
  `${candidate.sourceProvider}:${candidate.sourceDocketId}`;

const candidateStatus = (candidate: DocketCandidate): string =>
  candidate.dateTerminated ? 'Terminated' : 'Open';

const DocketBrowser = ({
  matterGuid,
  onLink,
  excludeKeys = [],
  hideHeader = false,
}: DocketBrowserProps) => {
  const { searchDockets } = useDockets(matterGuid);

  const [query, setQuery] = useState('');
  const [candidates, setCandidates] = useState<DocketCandidate[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [linkingKey, setLinkingKey] = useState<string | null>(null);

  // Guards against out-of-order responses: only the latest request may commit.
  const requestIdRef = useRef(0);

  useEffect(() => {
    const trimmed = query.trim();
    if (!trimmed) {
      requestIdRef.current += 1;
      setCandidates([]);
      setIsSearching(false);
      setSearchError(null);
      return;
    }

    const requestId = ++requestIdRef.current;
    const timer = window.setTimeout(async () => {
      setIsSearching(true);
      try {
        const results = await searchDockets(trimmed);
        if (requestIdRef.current !== requestId) return;
        setCandidates(results);
        setSearchError(null);
      } catch (err) {
        if (requestIdRef.current !== requestId) return;
        setCandidates([]);
        setSearchError(apiErrorMessage(err) ?? 'Could not search dockets. Please try again.');
      } finally {
        if (requestIdRef.current === requestId) setIsSearching(false);
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [query, searchDockets]);

  const results = useMemo(() => {
    const excluded = new Set(excludeKeys);
    return candidates.filter((candidate) => !excluded.has(candidateKey(candidate)));
  }, [candidates, excludeKeys]);

  const handleLink = async (candidate: DocketCandidate) => {
    setLinkingKey(candidateKey(candidate));
    try {
      await onLink(candidate);
    } finally {
      setLinkingKey(null);
    }
  };

  const hasQuery = query.trim().length > 0;

  return (
    <div className={cn('flex min-w-0 flex-col gap-5', !hideHeader && 'pt-4 md:pt-8')}>
      {!hideHeader && (
        <div className="flex flex-col gap-1">
          <h2 className="text-lg font-semibold tracking-tight text-foreground">
            Link a docket to this matter
          </h2>
          <p className="text-sm text-muted-foreground">
            Search CourtListener for US federal court dockets and link one to this matter.
          </p>
        </div>
      )}

      <Input
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Search by case name, number, or docket"
        className="h-11"
      />

      <div className="flex min-w-0 flex-col gap-3">
        {isSearching ? (
          <div className="flex items-center justify-center py-14">
            <Loading text="Searching dockets" />
          </div>
        ) : searchError ? (
          <div className="flex flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-border py-14 text-center">
            <p className="text-sm font-medium text-foreground">Search failed</p>
            <p className="text-sm text-muted-foreground">{searchError}</p>
          </div>
        ) : !hasQuery ? (
          <div className="flex flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-border py-14 text-center">
            <p className="text-sm font-medium text-foreground">Search for a docket</p>
            <p className="text-sm text-muted-foreground">
              Start typing a case name, number, or docket to find a match.
            </p>
          </div>
        ) : results.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-border py-14 text-center">
            <p className="text-sm font-medium text-foreground">No dockets found</p>
            <p className="text-sm text-muted-foreground">
              Try a different case name, number, or docket.
            </p>
          </div>
        ) : (
          results.map((candidate) => {
            const key = candidateKey(candidate);
            const status = candidateStatus(candidate);
            return (
              <Card
                key={key}
                className="flex w-full min-w-0 items-center justify-between gap-4 p-4 transition-shadow hover:shadow-md"
              >
                <div className="flex min-w-0 flex-col gap-1.5">
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="min-w-0 truncate text-sm font-semibold text-foreground">
                      {candidate.caseName ?? candidate.caseNumber ?? 'Untitled docket'}
                    </span>
                    <StatusPill
                      label={status}
                      tone={caseStatusTone(status)}
                      className="shrink-0"
                    />
                  </div>
                  <div className="flex min-w-0 items-center gap-x-2 text-xs text-muted-foreground">
                    {candidate.caseNumber && (
                      <>
                        <span className="font-mono text-foreground/70">{candidate.caseNumber}</span>
                        <span className="text-muted-foreground/40">•</span>
                      </>
                    )}
                    <span className="min-w-0 truncate">
                      {candidate.courtName ?? candidate.courtCode ?? 'Unknown court'}
                    </span>
                  </div>
                  <div className="truncate text-xs text-muted-foreground">
                    Filed {formatDocketDate(candidate.dateFiled)}
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  text="Link to this matter"
                  loading={linkingKey === key}
                  loadingText="Linking"
                  disabled={linkingKey !== null}
                  onClick={() => handleLink(candidate)}
                  className="shrink-0 whitespace-nowrap"
                />
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
};

export default DocketBrowser;
